import React, { useState, useEffect } from 'react';
import {
  fetchAdminUsers,
  updateAdminUser,
  createAdminUser,
  deleteAdminUser,
  fetchAdminUserVocabulary
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const SUPPORT_LABELS = {
  beginner: 'Začátečník',
  independent: 'Samostatně',
};

const EMPTY_CREATE_FORM = { username: '', email: '', password: '', is_admin: false };

const modalOverlayStyle = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
};

const modalBoxStyle = {
  backgroundColor: 'white', padding: '28px', borderRadius: '12px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)', minWidth: '340px', maxWidth: '90vw',
};

const inputStyle = {
  width: '100%', padding: '8px 12px', border: '1.5px solid #e5e7eb',
  borderRadius: '8px', fontSize: '0.92rem', marginBottom: '12px', boxSizing: 'border-box',
};

const primaryButtonStyle = {
  padding: '8px 18px', backgroundColor: '#3b82f6', color: 'white',
  border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem',
};

const secondaryButtonStyle = {
  padding: '8px 18px', backgroundColor: '#f3f4f6', color: '#374151',
  border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, fontSize: '0.88rem',
};

const rowButtonStyle = {
  padding: '5px 12px', borderRadius: '6px', border: '1.5px solid #e5e7eb',
  fontSize: '0.82rem', cursor: 'pointer', background: 'white', color: '#374151',
};

const UsersManager = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null); // userId being saved

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Edit modal
  const [editUser, setEditUser] = useState(null); // user object being edited
  const [editForm, setEditForm] = useState({ email: '', is_admin: false, password: '' });
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);

  // Delete confirmation modal
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Vocabulary modal
  const [vocabUser, setVocabUser] = useState(null);
  const [vocabWords, setVocabWords] = useState(null); // null = načítá se
  const [vocabError, setVocabError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchAdminUsers();
        setUsers(data);
      } catch (err) {
        setError('Nepodařilo se načíst uživatele.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSupportLevelChange = async (userId, newLevel) => {
    setSaving(userId);
    try {
      const updated = await updateAdminUser(userId, { support_level: newLevel });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, support_level: updated.support_level } : u));
    } catch (err) {
      alert('Nepodařilo se uložit změnu.');
    } finally {
      setSaving(null);
    }
  };

  // --- Vytvoření uživatele ---

  const handleOpenCreate = () => {
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateError(null);
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    if (!createForm.username.trim() || !createForm.email.trim() || !createForm.password.trim()) {
      setCreateError('Vyplň uživatelské jméno, e-mail i heslo.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const newUser = await createAdminUser({
        username: createForm.username.trim(),
        email: createForm.email.trim(),
        password: createForm.password,
        is_admin: createForm.is_admin,
      });
      setUsers(prev => [...prev, newUser]);
      setShowCreateModal(false);
    } catch (err) {
      // err.message obsahuje detail z backendu ("Username/Email already registered")
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // --- Úprava uživatele ---

  const handleOpenEdit = (u) => {
    setEditUser(u);
    setEditForm({ email: u.email, is_admin: u.is_admin, password: '' });
    setEditError(null);
  };

  const handleEditSave = async () => {
    if (!editForm.email.trim()) {
      setEditError('E-mail nesmí být prázdný.');
      return;
    }
    setEditing(true);
    setEditError(null);
    try {
      const payload = { email: editForm.email.trim(), is_admin: editForm.is_admin };
      if (editForm.password.trim()) {
        payload.password = editForm.password;
      }
      const updated = await updateAdminUser(editUser.id, payload);
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...updated } : u));
      setEditUser(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditing(false);
    }
  };

  // --- Smazání uživatele ---

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAdminUser(deleteUser.id);
      setUsers(prev => prev.filter(u => u.id !== deleteUser.id));
      setDeleteUser(null);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // --- Slovíčka uživatele ---

  const handleOpenVocabulary = async (u) => {
    setVocabUser(u);
    setVocabWords(null);
    setVocabError(null);
    try {
      const words = await fetchAdminUserVocabulary(u.id);
      setVocabWords(words);
    } catch (err) {
      setVocabError('Nepodařilo se načíst slovíčka.');
    }
  };

  const handleCloseVocabulary = () => {
    // Vyčistit stav, aby při otevření jiného uživatele nebliknula stará data.
    setVocabUser(null);
    setVocabWords(null);
    setVocabError(null);
  };

  if (loading) return <p style={{ color: '#666', padding: '20px' }}>Načítám uživatele...</p>;
  if (error) return <p style={{ color: '#dc3545', padding: '20px' }}>{error}</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 'bold', margin: 0, color: '#1f2937' }}>
          Správa uživatelů
        </h2>
        <button onClick={handleOpenCreate} style={primaryButtonStyle}>
          + Přidat uživatele
        </button>
      </div>

      <div style={{ borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', backgroundColor: '#f9fafb' }}>
              <th style={{ padding: '12px 16px', color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb' }}>Uživatel</th>
              <th style={{ padding: '12px 16px', color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb' }}>E-mail</th>
              <th style={{ padding: '12px 16px', color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb' }}>Role</th>
              <th style={{ padding: '12px 16px', color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb' }}>Úroveň podpory</th>
              <th style={{ padding: '12px 16px', color: '#6b7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb' }}>Akce</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, index) => (
              <React.Fragment key={u.id}>
                <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: index % 2 === 0 ? '#fff' : '#f9fafb', transition: 'background-color 0.15s ease' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                    {u.username}
                    {currentUser?.id === u.id && (
                      <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#9ca3af' }}>(vy)</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#666' }}>{u.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      background: u.is_admin ? '#fce8d5' : '#e8f4fd',
                      color: u.is_admin ? '#c0440a' : '#1a73e8',
                    }}>
                      {u.is_admin ? 'Admin' : 'Student'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <select
                      value={u.support_level || 'beginner'}
                      disabled={saving === u.id}
                      onChange={e => handleSupportLevelChange(u.id, e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1.5px solid #e5e7eb',
                        fontSize: '0.88rem',
                        cursor: saving === u.id ? 'wait' : 'pointer',
                        background: saving === u.id ? '#f9fafb' : 'white',
                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                        outline: 'none',
                      }}
                      onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
                      onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
                    >
                      {Object.entries(SUPPORT_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleOpenEdit(u)}
                        style={rowButtonStyle}
                        onMouseEnter={e => e.target.style.borderColor = '#3b82f6'}
                        onMouseLeave={e => e.target.style.borderColor = '#e5e7eb'}
                      >
                        Upravit
                      </button>
                      <button
                        onClick={() => handleOpenVocabulary(u)}
                        style={rowButtonStyle}
                        onMouseEnter={e => e.target.style.borderColor = '#10b981'}
                        onMouseLeave={e => e.target.style.borderColor = '#e5e7eb'}
                      >
                        Slovíčka
                      </button>
                      {currentUser?.id !== u.id && (
                        <button
                          onClick={() => { setDeleteUser(u); setDeleteError(null); }}
                          style={{ ...rowButtonStyle, color: '#dc2626' }}
                          onMouseEnter={e => e.target.style.borderColor = '#dc2626'}
                          onMouseLeave={e => e.target.style.borderColor = '#e5e7eb'}
                        >
                          Smazat
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal: přidat uživatele */}
      {showCreateModal && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Přidat uživatele</h3>
            <input
              type="text"
              placeholder="Uživatelské jméno"
              value={createForm.username}
              onChange={e => setCreateForm(f => ({ ...f, username: e.target.value }))}
              style={inputStyle}
              autoFocus
            />
            <input
              type="email"
              placeholder="E-mail"
              value={createForm.email}
              onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Heslo"
              value={createForm.password}
              onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
              style={inputStyle}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '0.9rem', color: '#374151', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={createForm.is_admin}
                onChange={e => setCreateForm(f => ({ ...f, is_admin: e.target.checked }))}
              />
              Administrátor
            </label>
            {createError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0 0 12px 0' }}>{createError}</p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateModal(false)} disabled={creating} style={secondaryButtonStyle}>
                Zrušit
              </button>
              <button onClick={handleCreate} disabled={creating} style={primaryButtonStyle}>
                {creating ? 'Vytvářím...' : 'Vytvořit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: upravit uživatele */}
      {editUser && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Upravit uživatele {editUser.username}</h3>
            <input
              type="email"
              placeholder="E-mail"
              value={editForm.email}
              onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
              style={inputStyle}
              autoFocus
            />
            <input
              type="password"
              placeholder="Nové heslo (nepovinné)"
              value={editForm.password}
              onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
              style={inputStyle}
            />
            <label style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px',
              fontSize: '0.9rem', color: currentUser?.id === editUser.id ? '#9ca3af' : '#374151',
              cursor: currentUser?.id === editUser.id ? 'not-allowed' : 'pointer',
            }}>
              <input
                type="checkbox"
                checked={editForm.is_admin}
                disabled={currentUser?.id === editUser.id}
                onChange={e => setEditForm(f => ({ ...f, is_admin: e.target.checked }))}
              />
              Administrátor
              {currentUser?.id === editUser.id && (
                <span style={{ fontSize: '0.78rem' }}>(vlastní práva nelze měnit)</span>
              )}
            </label>
            {editError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0 0 12px 0' }}>{editError}</p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditUser(null)} disabled={editing} style={secondaryButtonStyle}>
                Zrušit
              </button>
              <button onClick={handleEditSave} disabled={editing} style={primaryButtonStyle}>
                {editing ? 'Ukládám...' : 'Uložit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: potvrzení smazání */}
      {deleteUser && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <h3 style={{ margin: '0 0 12px 0', color: '#1f2937' }}>Smazat uživatele {deleteUser.username}?</h3>
            <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.9rem' }}>
              Smazání je nevratné — spolu s účtem se smaže i veškerý postup,
              slovíčka a deníkové záznamy tohoto uživatele.
            </p>
            {deleteError && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0 0 12px 0' }}>{deleteError}</p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteUser(null)} disabled={deleting} style={secondaryButtonStyle}>
                Zrušit
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                style={{ ...primaryButtonStyle, backgroundColor: '#dc2626' }}
              >
                {deleting ? 'Mažu...' : 'Smazat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: slovíčka uživatele */}
      {vocabUser && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalBoxStyle, maxWidth: '700px', width: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937' }}>Slovíčka uživatele {vocabUser.username}</h3>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {vocabError ? (
                <p style={{ color: '#dc2626', fontSize: '0.9rem' }}>{vocabError}</p>
              ) : vocabWords === null ? (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Načítám slovíčka...</p>
              ) : vocabWords.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Uživatel zatím nemá žádná slovíčka.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', backgroundColor: '#f9fafb' }}>
                      <th style={{ padding: '8px 12px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: '12px' }}>Anglicky</th>
                      <th style={{ padding: '8px 12px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: '12px' }}>Česky</th>
                      <th style={{ padding: '8px 12px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: '12px' }}>Téma</th>
                      <th style={{ padding: '8px 12px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: '12px' }}>Příklad</th>
                      <th style={{ padding: '8px 12px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: '12px' }}>Vytvořeno</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vocabWords.map((w) => (
                      <tr key={w.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{w.english_word}</td>
                        <td style={{ padding: '8px 12px' }}>{w.czech_translation}</td>
                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>{w.topic || '-'}</td>
                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>{w.example_sentence || '-'}</td>
                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                          {w.created_at ? new Date(w.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={handleCloseVocabulary} style={secondaryButtonStyle}>
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManager;
